import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modern Modal Component with accessibility and animations
 * @param {Object} props - Modal properties
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to close the modal
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.size - Modal size: 'sm', 'md', 'lg', 'xl', '2xl', '4xl', 'full'
 * @param {string} props.title - Modal title
 * @param {string} props.subtitle - Modal subtitle
 * @param {boolean} props.showCloseButton - Whether to show the close button
 * @param {string} props.overlayClassName - Custom overlay classes
 * @param {string} props.modalClassName - Custom modal classes
 * @param {string} props.headerClassName - Custom header classes
 * @param {React.ReactNode} props.headerActions - Custom header actions
 * @param {boolean} props.preventClose - Prevent closing on overlay click, escape or the header close button
 * @param {function} props.onCloseRequest - Called instead of onClose when the header close button is
 *   pressed while preventClose is set (e.g. to confirm discarding edits). Without it the button is disabled.
 * @param {string} props.theme - Theme variant: 'default', 'blue', 'green', 'purple', 'red'
 */
const Modal = ({
  isOpen,
  onClose,
  children,
  size = 'lg',
  title,
  ariaLabel,
  portal = false,
  subtitle,
  showCloseButton = true,
  overlayClassName = '',
  modalClassName = '',
  headerClassName = '',
  headerActions,
  preventClose = false,
  onCloseRequest,
  theme = 'default'
}) => {
  const titleId = useId();
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const closePolicy = useRef({ onClose, preventClose });
  closePolicy.current = { onClose, preventClose };

  // Size mappings
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    full: 'max-w-full mx-4'
  };

  // Theme mappings with dark mode support
  const themeClasses = {
    default: {
      overlay: 'bg-gray-900/75 dark:bg-black/80',
      modal: 'bg-white dark:bg-gray-800',
      header: 'bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800 dark:to-gray-700 border-gray-200 dark:border-gray-600',
      closeButton: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700',
      title: 'text-gray-900 dark:text-white',
      subtitle: 'text-gray-600 dark:text-gray-300'
    },
    blue: {
      overlay: 'bg-blue-900/75 dark:bg-black/80',
      modal: 'bg-white dark:bg-gray-800',
      header: 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/50 dark:to-indigo-900/50 border-blue-200 dark:border-blue-700',
      closeButton: 'text-blue-400 hover:text-blue-600 hover:bg-blue-100 dark:text-blue-300 dark:hover:text-blue-100 dark:hover:bg-blue-900/50',
      title: 'text-gray-900 dark:text-white',
      subtitle: 'text-gray-600 dark:text-gray-300'
    },
    green: {
      overlay: 'bg-green-900/75 dark:bg-black/80',
      modal: 'bg-white dark:bg-gray-800',
      header: 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/50 dark:to-emerald-900/50 border-green-200 dark:border-green-700',
      closeButton: 'text-green-400 hover:text-green-600 hover:bg-green-100 dark:text-green-300 dark:hover:text-green-100 dark:hover:bg-green-900/50',
      title: 'text-gray-900 dark:text-white',
      subtitle: 'text-gray-600 dark:text-gray-300'
    },
    purple: {
      overlay: 'bg-purple-900/75 dark:bg-black/80',
      modal: 'bg-white dark:bg-gray-800',
      header: 'bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/50 dark:to-violet-900/50 border-purple-200 dark:border-purple-700',
      closeButton: 'text-purple-400 hover:text-purple-600 hover:bg-purple-100 dark:bg-purple-900/50 dark:text-purple-300 dark:hover:text-purple-100 dark:hover:bg-purple-900/50',
      title: 'text-gray-900 dark:text-white',
      subtitle: 'text-gray-600 dark:text-gray-300'
    },
    red: {
      overlay: 'bg-red-900/75 dark:bg-black/80',
      modal: 'bg-white dark:bg-gray-800',
      header: 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/50 dark:to-rose-900/50 border-red-200 dark:border-red-700',
      closeButton: 'text-red-400 hover:text-red-600 hover:bg-red-100 dark:text-red-300 dark:hover:text-red-100 dark:hover:bg-red-900/50',
      title: 'text-gray-900 dark:text-white',
      subtitle: 'text-gray-600 dark:text-gray-300'
    }
  };

  const currentTheme = themeClasses[theme] || themeClasses.default;

  // Handle escape key press
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Upload/confirmation dialogs can be portaled above another Modal.
      // Only the top dialog owns Escape, including when it blocks dismissal.
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      if (dialogs[dialogs.length - 1] !== modalRef.current?.closest('[role="dialog"]')) return;
      e.preventDefault();
      if (!closePolicy.current.preventClose) closePolicy.current.onClose?.();
    };

    const previousOverflow = document.body.style.overflow;
    // Capture once per opening, not whenever callbacks or editing locks change.
    previousFocusRef.current = document.activeElement;
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    modalRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
      
      // Restore focus
      if (previousFocusRef.current?.isConnected) {
        previousFocusRef.current.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  // The header X follows the same guard as Escape and the backdrop. A caller
  // that blocks closing can ask first (onCloseRequest); otherwise the X is
  // disabled, so it can never silently discard what preventClose protects.
  const handleCloseButton = () => {
    if (!preventClose) onClose();
    else onCloseRequest?.();
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !preventClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const content = (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 animate-modal-overlay ${currentTheme.overlay} ${overlayClassName}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? ariaLabel : undefined}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`
          relative w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col
          ${currentTheme.modal} rounded-2xl shadow-2xl
          transform transition-all duration-300 scale-100 opacity-100
          animate-modal-content
          ${modalClassName}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || subtitle || showCloseButton || headerActions) && (
          <div className={`
            px-6 py-4 border-b ${currentTheme.header} shrink-0
            rounded-t-2xl flex items-center justify-between
            ${headerClassName}
          `}>
            <div className="flex-1 min-w-0">
              {title && (
                <h2
                  id={titleId}
                  className={`text-xl font-bold truncate ${currentTheme.title}`}
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className={`text-sm mt-1 truncate ${currentTheme.subtitle}`}>
                  {subtitle}
                </p>
              )}
            </div>
            
            <div className="flex items-center space-x-2 ml-4">
              {headerActions}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={handleCloseButton}
                  disabled={preventClose && !onCloseRequest}
                  className={`
                    p-2 rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                    ${currentTheme.closeButton}
                  `}
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
  return portal ? createPortal(content, document.body) : content;
};

export default Modal;
